import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDailyStory, illustrateStory, BACKEND } from "../lib/api";
import { Loader, SectionTitle } from "../components/Shared";
import { useProfile } from "../context/ProfileContext";
import { Image as ImageIcon, Share2, Check, MessageCircle, ArrowRight, Sun, Mail } from "lucide-react";

export default function Today() {
  const { touch, incr } = useProfile();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [illusLoading, setIllusLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const counted = React.useRef(false);

  useEffect(() => {
    getDailyStory()
      .then((d) => {
        setData(d);
        if (!counted.current) {
          counted.current = true;
          touch();
          incr("storiesRead");
        }
      })
      .catch(() => setErr("Could not load today's story. Please refresh."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const illustrate = async () => {
    if (!data?.story_id) return;
    setIllusLoading(true);
    try {
      const r = await illustrateStory(data.story_id);
      setData((d) => ({ ...d, illustration_url: r.illustration_url }));
    } catch (e) {} finally { setIllusLoading(false); }
  };

  const ogUrl = data?.story_id ? `${BACKEND}/api/og/story/${data.story_id}` : "";

  const share = async () => {
    if (!ogUrl) return;
    try {
      await navigator.clipboard.writeText(ogUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      window.prompt("Copy this link:", ogUrl);
    }
  };

  const shareWhatsApp = () => {
    if (!ogUrl) return;
    const text = `Today's story from EduSphere ✨ ${data.title}\n${ogUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const imgSrc = data?.illustration_url ? `${BACKEND}${data.illustration_url}` : null;
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric"
  });

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="today-page">
      <SectionTitle
        kicker={`Story of the day · ${todayLabel}`}
        title="A fresh tale, every day."
        subtitle="One story curated each morning — mythology, science, or a moral classic. Read in 3 minutes, share in one tap."
      />

      {loading && (
        <div className="card p-10 grid place-items-center" data-testid="today-loading">
          <Sun size={28} className="text-[#E07A5F] mb-3" />
          <Loader label="Crafting today's story…" />
        </div>
      )}
      {err && <div className="text-[#D9534F]" data-testid="today-error">{err}</div>}

      {data && (
        <article className="card p-7 md:p-10 fadeIn" data-testid="today-content">
          {imgSrc && (
            <div className="mb-6 rounded-2xl overflow-hidden border border-[#DCD7CE]">
              <img src={imgSrc} alt={data.title} className="w-full h-auto block" data-testid="today-illustration" />
            </div>
          )}
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold mb-3">Today's topic · {data.topic}</div>
          <h1 className="font-story text-3xl md:text-5xl font-semibold text-[#1A1A1A] leading-tight">{data.title}</h1>
          <p className="font-story text-[19px] md:text-[21px] leading-[1.7] text-[#1A1A1A] mt-6 whitespace-pre-wrap">{data.story}</p>

          <div className="mt-7 flex flex-wrap gap-3" data-testid="today-actions">
            {!imgSrc && (
              <button onClick={illustrate} disabled={illusLoading} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60" data-testid="today-illustrate">
                <ImageIcon size={16} />
                {illusLoading ? "Illustrating…" : "Generate illustration"}
              </button>
            )}
            <button onClick={share} className="btn-outline inline-flex items-center gap-2" data-testid="today-share">
              {copied ? <Check size={16} /> : <Share2 size={16} />}
              {copied ? "Link copied!" : "Share"}
            </button>
            <button
              onClick={shareWhatsApp}
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white rounded-full px-5 py-2.5 font-semibold transition"
              data-testid="today-share-whatsapp"
            >
              <MessageCircle size={16} /> WhatsApp
            </button>
            <Link to="/chat" className="btn-outline inline-flex items-center gap-2" data-testid="today-discuss">
              Ask follow-up <ArrowRight size={14} />
            </Link>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <div className="bg-[#FBE7DF] rounded-2xl p-5 border border-[#DCD7CE]" data-testid="today-moral">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#E07A5F] font-semibold mb-2">Moral</div>
              <div className="text-[#1A1A1A]">{data.moral}</div>
            </div>
            <div className="bg-[#EAECE6] rounded-2xl p-5 border border-[#DCD7CE]" data-testid="today-reflection">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold mb-2">Think with me</div>
              <div className="text-[#1A1A1A]">{data.reflection_question}</div>
            </div>
          </div>

          {/* Subscribe CTA */}
          <div className="mt-8 rounded-2xl border border-[#DCD7CE] bg-gradient-to-br from-[#FBE7DF] to-[#EAECE6] p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="today-subscribe-cta">
            <div className="flex gap-3 items-start">
              <div className="w-10 h-10 rounded-full bg-white border border-[#DCD7CE] grid place-items-center text-[#E07A5F] flex-shrink-0">
                <Mail size={18} />
              </div>
              <div>
                <div className="font-heading font-bold text-[#1A1A1A]">Get tomorrow's story before breakfast.</div>
                <div className="text-sm text-[#4A4A4A]">One illustrated tale a day, in your inbox at 7 AM IST.</div>
              </div>
            </div>
            <Link to="/subscribe" className="btn-primary inline-flex items-center gap-2 whitespace-nowrap" data-testid="today-subscribe-link">
              Subscribe free <ArrowRight size={14} />
            </Link>
          </div>
        </article>
      )}
    </div>
  );
}
